import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Button,
  SpaceBetween,
  Badge,
  Box,
  Pagination,
  TextFilter,
  Alert,
  Modal,
  FormField,
  Textarea,
  ColumnLayout,
  Spinner
} from '@cloudscape-design/components';
import { dynamodb, s3, config } from '../aws-config';

function ValidationView({ onValidationComplete }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteringText, setFilteringText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [alert, setAlert] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [confidenceThreshold, setConfidenceThreshold] = useState(85);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  const pageSize = 10;

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const result = await dynamodb.scan({ 
        TableName: config.submissionsTable,
        Limit: 100 
      }).promise();

      console.log('Loaded submissions:', result.Items);

      // Filter documents that need validation (below threshold OR requires QC)
      const needsValidation = (result.Items || [])
        .filter(item => {
          // Handle both number and Decimal types from DynamoDB
          const confidence = typeof item.confidence === 'number' 
            ? item.confidence 
            : (item.confidence ? parseFloat(item.confidence) : 0);
          const businessRulesScore = typeof item.businessRulesScore === 'number'
            ? item.businessRulesScore
            : (item.businessRulesScore ? parseFloat(item.businessRulesScore) : 100);
          const isComplete = item.processingStatus === 'COMPLETE';
          const needsReview = confidence < confidenceThreshold || item.requiresQC || businessRulesScore < 100;
          
          console.log(`Item ${item.submissionId}: confidence=${confidence}, businessRules=${businessRulesScore}, complete=${isComplete}, needsReview=${needsReview}, requiresQC=${item.requiresQC}`);
          
          return needsReview && isComplete;
        })
        .map(item => ({
          ...item,
          displayName: item.s3Key?.split('/').pop() || item.submissionId || 'Unknown',
          confidenceScore: typeof item.confidence === 'number' 
            ? item.confidence 
            : (item.confidence ? parseFloat(item.confidence) : 0),
          businessRulesScore: typeof item.businessRulesScore === 'number'
            ? item.businessRulesScore
            : (item.businessRulesScore ? parseFloat(item.businessRulesScore) : 100),
          reviewStatus: item.reviewStatus || 'PENDING',
          requiresQC: item.requiresQC || false,
          ruleViolations: item.ruleViolations || []
        }))
        .sort((a, b) => a.confidenceScore - b.confidenceScore); // Lowest confidence first

      console.log('Documents needing validation:', needsValidation);
      setDocuments(needsValidation);
    } catch (error) {
      console.error('Error loading documents:', error);
      setAlert({ type: 'error', content: `Failed to load documents: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // In a real app, load threshold from settings
    const savedThreshold = localStorage.getItem('confidenceThreshold');
    if (savedThreshold) {
      setConfidenceThreshold(parseFloat(savedThreshold));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (item) => {
    try {
      const submissions_table = dynamodb.Table(config.submissionsTable);
      
      await submissions_table.update({
        Key: { submissionId: item.submissionId },
        UpdateExpression: 'SET reviewStatus = :status, reviewedAt = :timestamp, reviewNotes = :notes',
        ExpressionAttributeValues: {
          ':status': 'APPROVED',
          ':timestamp': new Date().toISOString(),
          ':notes': reviewNotes || 'Approved by reviewer'
        }
      }).promise();

      setAlert({ type: 'success', content: 'Document approved successfully' });
      setShowReviewModal(false);
      setReviewNotes('');
      setSelectedItems([]);
      setDocumentUrl(null);
      loadDocuments();
      if (onValidationComplete) onValidationComplete();
    } catch (error) {
      console.error('Approve error:', error);
      setAlert({ type: 'error', content: `Failed to approve: ${error.message}` });
    }
  };

  const handleReject = async (item) => {
    try {
      const submissions_table = dynamodb.Table(config.submissionsTable);
      
      await submissions_table.update({
        Key: { submissionId: item.submissionId },
        UpdateExpression: 'SET reviewStatus = :status, reviewedAt = :timestamp, reviewNotes = :notes',
        ExpressionAttributeValues: {
          ':status': 'REJECTED',
          ':timestamp': new Date().toISOString(),
          ':notes': reviewNotes || 'Rejected by reviewer'
        }
      }).promise();

      setAlert({ type: 'success', content: 'Document rejected' });
      setShowReviewModal(false);
      setReviewNotes('');
      setSelectedItems([]);
      setDocumentUrl(null);
      loadDocuments();
      if (onValidationComplete) onValidationComplete();
    } catch (error) {
      console.error('Reject error:', error);
      setAlert({ type: 'error', content: `Failed to reject: ${error.message}` });
    }
  };

  const loadDocumentPreview = async (item) => {
    if (!item.s3Key) {
      setAlert({ type: 'warning', content: 'No document file available for preview' });
      return;
    }

    setLoadingDocument(true);
    try {
      // Use bucket name from DynamoDB item, fallback to config
      const bucketName = item.s3Bucket || config.bucketName;
      
      console.log('Loading document preview:');
      console.log('  Bucket:', bucketName);
      console.log('  Key:', item.s3Key);
      console.log('  Item:', item);
      
      // Verify S3 client is configured
      console.log('S3 Config:', {
        region: s3.config.region,
        credentials: s3.config.credentials ? 'Present' : 'Missing'
      });
      
      // Generate a presigned URL for the document
      const params = {
        Bucket: bucketName,
        Key: item.s3Key,
        Expires: 3600 // URL expires in 1 hour
      };

      console.log('Generating presigned URL with params:', params);
      
      const url = await s3.getSignedUrlPromise('getObject', params);
      
      console.log('Successfully generated presigned URL:', url.substring(0, 100) + '...');
      setDocumentUrl(url);
    } catch (error) {
      console.error('Error loading document:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        bucket: item.s3Bucket || config.bucketName,
        key: item.s3Key
      });
      setAlert({ 
        type: 'error', 
        content: `Failed to load document: ${error.message}. Check console for details.` 
      });
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleReviewClick = (item) => {
    setSelectedItems([item]);
    setShowReviewModal(true);
    loadDocumentPreview(item);
  };

  const getConfidenceBadge = (score) => {
    if (score >= 90) return <Badge color="green">{score.toFixed(1)}%</Badge>;
    if (score >= 75) return <Badge color="blue">{score.toFixed(1)}%</Badge>;
    if (score >= 50) return <Badge color="grey">{score.toFixed(1)}%</Badge>;
    return <Badge color="red">{score.toFixed(1)}%</Badge>;
  };

  const getBusinessRulesBadge = (score) => {
    if (score === 100) return <Badge color="green">100% Pass</Badge>;
    if (score >= 90) return <Badge color="blue">{score.toFixed(0)}%</Badge>;
    if (score >= 75) return <Badge color="grey">{score.toFixed(0)}%</Badge>;
    return <Badge color="red">{score.toFixed(0)}%</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'grey', text: 'Pending Review' },
      APPROVED: { color: 'green', text: 'Approved' },
      REJECTED: { color: 'red', text: 'Rejected' },
      IN_REVIEW: { color: 'blue', text: 'In Review' }
    };
    const config = statusConfig[status] || { color: 'grey', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const filteredDocuments = documents.filter(doc =>
    doc.displayName?.toLowerCase().includes(filteringText.toLowerCase()) ||
    doc.submissionId?.toLowerCase().includes(filteringText.toLowerCase())
  );

  const paginatedDocuments = filteredDocuments.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const columnDefinitions = [
    {
      id: 'displayName',
      header: 'Document Name',
      cell: item => (
        <Box>
          <Box fontWeight="bold">{item.displayName}</Box>
          <Box fontSize="body-s" color="text-body-secondary">
            ID: {item.submissionId?.substring(0, 12)}...
          </Box>
        </Box>
      ),
      sortingField: 'displayName',
      width: 300
    },
    {
      id: 'confidence',
      header: 'Confidence Score',
      cell: item => (
        <Box>
          {getConfidenceBadge(item.confidenceScore)}
          <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxxs' }}>
            {item.confidenceAnalysis?.summary ? 
              item.confidenceAnalysis.summary.split('-')[0].trim() : 
              `Threshold: ${confidenceThreshold}%`}
          </Box>
        </Box>
      ),
      sortingField: 'confidenceScore',
      width: 180
    },
    {
      id: 'businessRules',
      header: 'Business Rules',
      cell: item => (
        <Box>
          {getBusinessRulesBadge(item.businessRulesScore)}
          {item.requiresQC && (
            <Box margin={{ top: 'xxxs' }}>
              <Badge color="red">QC Required</Badge>
            </Box>
          )}
          {item.ruleViolations && item.ruleViolations.length > 0 && (
            <Box fontSize="body-s" color="text-status-error" margin={{ top: 'xxxs' }}>
              {item.ruleViolations.length} violation{item.ruleViolations.length !== 1 ? 's' : ''}
            </Box>
          )}
        </Box>
      ),
      sortingField: 'businessRulesScore',
      width: 150
    },
    {
      id: 'uploadTimestamp',
      header: 'Submitted',
      cell: item => item.uploadTimestamp ? new Date(item.uploadTimestamp).toLocaleString() : '-',
      sortingField: 'uploadTimestamp',
      width: 180
    },
    {
      id: 'reviewStatus',
      header: 'Review Status',
      cell: item => getStatusBadge(item.reviewStatus),
      width: 150
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <Button
          variant="primary"
          iconName="check"
          iconAlign="left"
          onClick={() => handleReviewClick(item)}
        >
          Review
        </Button>
      ),
      minWidth: 160
    }
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={`Documents with confidence below ${confidenceThreshold}% require manual validation`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="refresh"
                onClick={loadDocuments}
                loading={loading}
              >
                Refresh
              </Button>
            </SpaceBetween>
          }
        >
          Validation Queue
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {alert && (
          <Alert
            type={alert.type}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.content}
          </Alert>
        )}

        <Alert type="info" header="Understanding Validation Scores">
          <SpaceBetween direction="vertical" size="xs">
            <Box>
              <Box fontWeight="bold">Confidence Score:</Box> Indicates how accurately the system extracted data from your forms based on text clarity, form structure recognition, and field completeness.
            </Box>
            <Box>
              <Box fontWeight="bold">Business Rules Score:</Box> Shows compliance with Navy personnel evaluation standards. Any rule violations automatically trigger a Quality Control review.
            </Box>
            <Box>
              Documents below {confidenceThreshold}% confidence or with business rule violations require manual review to ensure accuracy and compliance.
            </Box>
          </SpaceBetween>
        </Alert>

        {documents.length === 0 && !loading && (
          <Alert type="success">
            No documents require validation. All submissions meet the confidence threshold.
          </Alert>
        )}

        <Table
          columnDefinitions={columnDefinitions}
          items={paginatedDocuments}
          loading={loading}
          loadingText="Loading documents..."
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          selectionType="single"
          header={
            <Header
              counter={`(${filteredDocuments.length})`}
              description="Documents requiring manual review"
            >
              Pending Validation
            </Header>
          }
          filter={
            <TextFilter
              filteringText={filteringText}
              onChange={({ detail }) => setFilteringText(detail.filteringText)}
              filteringPlaceholder="Search documents..."
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              pagesCount={Math.ceil(filteredDocuments.length / pageSize)}
            />
          }
          empty={
            <Box textAlign="center" color="inherit">
              <b>No documents need validation</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                All submissions meet the confidence threshold.
              </Box>
            </Box>
          }
        />

        <Modal
          visible={showReviewModal}
          onDismiss={() => {
            setShowReviewModal(false);
            setReviewNotes('');
            setDocumentUrl(null);
          }}
          size="max"
          header={
            <Header variant="h2">
              Review Document
            </Header>
          }
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => {
                    setShowReviewModal(false);
                    setReviewNotes('');
                    setDocumentUrl(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="normal"
                  iconName="close"
                  onClick={() => selectedItems[0] && handleReject(selectedItems[0])}
                >
                  Reject
                </Button>
                <Button
                  variant="primary"
                  iconName="check"
                  onClick={() => selectedItems[0] && handleApprove(selectedItems[0])}
                >
                  Approve
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          {selectedItems[0] && (
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween direction="vertical" size="l">
                <Container>
                  <SpaceBetween direction="vertical" size="m">
                    <div>
                      <Box variant="awsui-key-label">Document Name</Box>
                      <Box fontSize="heading-m" fontWeight="bold" padding={{ top: 'xxxs' }}>
                        {selectedItems[0].displayName}
                      </Box>
                    </div>
                    
                    <div>
                      <Box variant="awsui-key-label">Submission ID</Box>
                      <Box fontSize="body-m" padding={{ top: 'xxxs' }}>
                        {selectedItems[0].submissionId}
                      </Box>
                    </div>

                    <div>
                      <Box variant="awsui-key-label">Confidence Score</Box>
                      <Box padding={{ top: 'xxxs' }}>
                        {getConfidenceBadge(selectedItems[0].confidenceScore)}
                      </Box>
                      {selectedItems[0].confidenceAnalysis && (
                        <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xs' }}>
                          {selectedItems[0].confidenceAnalysis.summary}
                        </Box>
                      )}
                    </div>

                    <div>
                      <Box variant="awsui-key-label">Business Rules Score</Box>
                      <Box padding={{ top: 'xxxs' }}>
                        <SpaceBetween direction="horizontal" size="xs">
                          {getBusinessRulesBadge(selectedItems[0].businessRulesScore)}
                          {selectedItems[0].requiresQC && <Badge color="red">QC Required</Badge>}
                        </SpaceBetween>
                      </Box>
                      {selectedItems[0].ruleViolations && selectedItems[0].ruleViolations.length > 0 && (
                        <Box fontSize="body-s" color="text-status-error" padding={{ top: 'xs' }}>
                          {selectedItems[0].ruleViolations.length} rule violation{selectedItems[0].ruleViolations.length !== 1 ? 's' : ''} detected
                        </Box>
                      )}
                    </div>

                    {selectedItems[0].confidenceAnalysis && selectedItems[0].confidenceAnalysis.factors && (
                      <div>
                        <Box variant="awsui-key-label">Confidence Breakdown</Box>
                        <SpaceBetween direction="vertical" size="xs">
                          {selectedItems[0].confidenceAnalysis.factors.map((factor, idx) => (
                            <Box key={idx} padding={{ top: 'xxxs' }}>
                              <Box fontSize="body-s" fontWeight="bold">
                                {factor.category}: {factor.score}%
                              </Box>
                              <Box fontSize="body-s" color="text-body-secondary">
                                {factor.description}
                              </Box>
                            </Box>
                          ))}
                        </SpaceBetween>
                      </div>
                    )}

                    {selectedItems[0].confidenceAnalysis && selectedItems[0].confidenceAnalysis.recommendations && selectedItems[0].confidenceAnalysis.recommendations.length > 0 && (
                      <div>
                        <Box variant="awsui-key-label">Recommendations</Box>
                        <SpaceBetween direction="vertical" size="xxs">
                          {selectedItems[0].confidenceAnalysis.recommendations.map((rec, idx) => (
                            <Box key={idx} fontSize="body-s" color="text-status-warning" padding={{ top: 'xxxs' }}>
                              • {rec}
                            </Box>
                          ))}
                        </SpaceBetween>
                      </div>
                    )}

                    {selectedItems[0].ruleViolations && selectedItems[0].ruleViolations.length > 0 && (
                      <div>
                        <Box variant="awsui-key-label">Business Rule Violations</Box>
                        <SpaceBetween direction="vertical" size="xs">
                          {selectedItems[0].ruleViolations
                            .sort((a, b) => {
                              // Sort by rule number numerically
                              const aNum = parseInt(a.ruleNumber || a.ruleId.replace(/\D/g, '')) || 0;
                              const bNum = parseInt(b.ruleNumber || b.ruleId.replace(/\D/g, '')) || 0;
                              return aNum - bNum;
                            })
                            .map((violation, idx) => (
                            <Container key={idx}>
                              <SpaceBetween direction="vertical" size="xxs">
                                <Box>
                                  <SpaceBetween direction="horizontal" size="xs">
                                    <Badge color="red">{violation.ruleId}</Badge>
                                    <Badge color={
                                      violation.severity === 'CRITICAL' ? 'red' :
                                      violation.severity === 'HIGH' ? 'red' :
                                      violation.severity === 'MEDIUM' ? 'blue' : 'grey'
                                    }>
                                      {violation.severity}
                                    </Badge>
                                    {violation.autoQC && <Badge color="red">Auto QC</Badge>}
                                    {violation.block && (
                                      <Badge color="grey">Block {violation.block}</Badge>
                                    )}
                                  </SpaceBetween>
                                </Box>
                                <Box fontSize="body-s" fontWeight="bold">
                                  {violation.title}
                                </Box>
                                <Box fontSize="body-s" color="text-status-error">
                                  {violation.message}
                                </Box>
                                {violation.details && (
                                  <Box padding={{ top: 'xs' }}>
                                    <Box fontSize="body-s" fontWeight="bold" color="text-body-secondary">
                                      Details:
                                    </Box>
                                    {violation.details.formValue && violation.details.databaseValue && (
                                      <ColumnLayout columns={2} variant="text-grid">
                                        <div>
                                          <Box fontSize="body-s" color="text-body-secondary">Form Value:</Box>
                                          <Box fontSize="body-s" fontWeight="bold" color="text-status-error">
                                            {violation.details.formValue}
                                          </Box>
                                        </div>
                                        <div>
                                          <Box fontSize="body-s" color="text-body-secondary">Expected (OMPF):</Box>
                                          <Box fontSize="body-s" fontWeight="bold" color="text-status-success">
                                            {violation.details.databaseValue}
                                          </Box>
                                        </div>
                                      </ColumnLayout>
                                    )}
                                    {violation.details.source && (
                                      <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
                                        Source: {violation.details.source}
                                      </Box>
                                    )}
                                    {violation.details.reason && (
                                      <Box fontSize="body-s" color="text-body-secondary" padding={{ top: 'xxs' }}>
                                        Reason: {violation.details.reason}
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </SpaceBetween>
                            </Container>
                          ))}
                        </SpaceBetween>
                      </div>
                    )}

                    <div>
                      <Box variant="awsui-key-label">Submitted</Box>
                      <Box fontSize="body-m" padding={{ top: 'xxxs' }}>
                        {selectedItems[0].uploadTimestamp 
                          ? new Date(selectedItems[0].uploadTimestamp).toLocaleString()
                          : 'Unknown'}
                      </Box>
                    </div>

                    {selectedItems[0].extractedText && (
                      <div>
                        <Box variant="awsui-key-label">Extracted Text Preview</Box>
                        <Box 
                          fontSize="body-s" 
                          padding={{ top: 'xxxs' }}
                          color="text-body-secondary"
                        >
                          {selectedItems[0].extractedText.substring(0, 300)}...
                        </Box>
                      </div>
                    )}
                  </SpaceBetween>
                </Container>

                <FormField
                  label="Review Notes"
                  description="Add notes about your review decision (optional)"
                >
                  <Textarea
                    value={reviewNotes}
                    onChange={({ detail }) => setReviewNotes(detail.value)}
                    placeholder="Enter review notes, reasons for approval/rejection, or any observations..."
                    rows={6}
                  />
                </FormField>
              </SpaceBetween>

              <Container
                header={
                  <Header variant="h3">
                    Document Preview
                  </Header>
                }
              >
                {loadingDocument && (
                  <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                    <Spinner size="large" />
                    <Box padding={{ top: 's' }} color="text-body-secondary">
                      Loading document...
                    </Box>
                  </Box>
                )}
                
                {!loadingDocument && documentUrl && (
                  <div style={{ height: '600px', overflow: 'auto', border: '1px solid #e9ebed' }}>
                    {selectedItems[0]?.fileType === 'pdf' ? (
                      <iframe
                        src={`${documentUrl}#view=FitH`}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        title="Document Preview"
                      />
                    ) : (
                      <img
                        src={documentUrl}
                        alt="Document Preview"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block'
                        }}
                        onError={(e) => {
                          console.error('Image load error:', e);
                          setAlert({ type: 'error', content: 'Failed to load image preview' });
                        }}
                      />
                    )}
                  </div>
                )}

                {!loadingDocument && !documentUrl && (
                  <Box textAlign="center" padding={{ vertical: 'xxl' }} color="text-body-secondary">
                    <Box fontSize="heading-m" padding={{ bottom: 's' }}>
                      No preview available
                    </Box>
                    <Box>
                      Unable to load document preview
                    </Box>
                  </Box>
                )}
              </Container>
            </ColumnLayout>
          )}
        </Modal>
      </SpaceBetween>
    </Container>
  );
}

export default ValidationView;
